import { useCallback, useMemo } from 'react';
import { isEmpty, isNil } from 'lodash';
import { LookingGlassSummaryData } from 'api/models/LookingGlassSummaryData';
import { Rows } from 'common/types/scalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { DAILY_NAV_COLUMN_ORDER, DAILY_NAV_SPREADSHEET_NAME } from 'pages/Intelligence/DailyNav/dailyNav.constants';
import { getArrayValue } from 'utilities';
import { createColumns, createParser, createRows } from './config';
import {
  DailyNavColumn,
  DailyNavSpreadsheet,
  DailyNavTableRow,
  UseDailyNavSheetParams,
  UseDailyNavSheetResult,
} from './useDailyNavSheet.types';

const useDailyNavSheet = (params: UseDailyNavSheetParams): UseDailyNavSheetResult => {
  const { companies, firmSlug, records, previousValuations, onReviewClick, canReview } = params;

  // The endpoint already returns one row per company (the latest FINAL company rollup
  // before the report date), so the sheet only keys them by company_id.
  const previousValuationByCompany = useMemo(() => {
    const byCompany = new Map<number, LookingGlassSummaryData>();
    getArrayValue(previousValuations).forEach(row => {
      if (!isNil(row.company_id)) {
        byCompany.set(row.company_id, row);
      }
    });
    return byCompany;
  }, [previousValuations]);

  const dailyNavRows = useMemo<DailyNavTableRow[]>(
    () =>
      getArrayValue(records).reduce<DailyNavTableRow[]>((accumulator, record) => {
        const company = getArrayValue(companies).find(item => item.company_id === record.company);
        if (!company) {
          return accumulator;
        }

        accumulator.push({
          companyId: record.company,
          companyName: company.company_name,
          companySlug: company.company_slug,
          record,
          lookingGlass: previousValuationByCompany.get(record.company),
        });
        return accumulator;
      }, []),
    [companies, previousValuationByCompany, records]
  );

  const rows = useMemo<Rows>(() => createRows({ firmSlug, rows: dailyNavRows }), [dailyNavRows, firmSlug]);

  const columns = useMemo<DailyNavColumn[]>(() => {
    if (isEmpty(dailyNavRows)) {
      return [];
    }
    return createColumns(dailyNavRows);
  }, [dailyNavRows]);

  const parser = useCallback(
    () =>
      createParser({
        columns,
        rowConfig: rows,
        dailyNavRows,
        onReviewClick,
        columnTypeOrder: DAILY_NAV_COLUMN_ORDER,
        canReview,
      }),
    [columns, rows, dailyNavRows, onReviewClick, canReview]
  );

  const spreadsheet = useMemo<DailyNavSpreadsheet | null>(() => {
    if (isEmpty(columns)) {
      return null;
    }

    // Constructed from a plain config object rather than satisfying SpreadsheetConfigConstructorParams' full
    // generic signature exactly, matching the same pattern used by useFirmSummarySheet.
    const spreadsheetConfig = new SpreadsheetConfig({
      allowConfirmAndDeleteColumn: false,
      allowDeleteRow: false,
      // Row 0 is the section band (Previous Valuation / NAV Considerations); row 1 holds the column labels.
      colTitleRow: 1,
      columns,
      hasColTitle: true,
      name: DAILY_NAV_SPREADSHEET_NAME,
      page: DAILY_NAV_SPREADSHEET_NAME,
      parser,
      reverseParser: undefined,
      rowConfig: rows,
      showPreviousColsDivider: false,
      showTitlesColumn: true,
      showToolbar: true,
      showTotalColumn: false,
      tableData: { columns },
      totalParser: undefined,
    } as unknown as ConstructorParameters<typeof SpreadsheetConfig>[0]) as DailyNavSpreadsheet;

    return spreadsheetConfig;
  }, [columns, parser, rows]);

  return {
    spreadsheet,
  };
};

export default useDailyNavSheet;
